"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@meetploy/storybook";

const TOAST_DURATION = 5000;

function ToastProvider({ children }: { children?: React.ReactNode }) {
	return <>{children}</>;
}

const ToastViewport = ({
	ref,
	className,
	...props
}: React.ComponentProps<"ol"> & {
	ref?: React.RefObject<HTMLOListElement | null>;
}) => (
	<ol
		ref={ref}
		role="region"
		aria-label="Notifications"
		aria-live="polite"
		tabIndex={-1}
		className={cn(
			"pointer-events-none fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
			className,
		)}
		{...props}
	/>
);
ToastViewport.displayName = "ToastViewport";

const toastVariants = cva(
	"group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-none border p-4 pr-6 shadow-sm transition-all data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=closed]:fill-mode-forwards data-[state=closed]:pointer-events-none",
	{
		variants: {
			variant: {
				default: "border-border bg-background text-foreground",
				destructive:
					"destructive group border-destructive bg-destructive text-destructive-foreground",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

interface ToastRootProps
	extends Omit<React.ComponentProps<"li">, "onChange">,
		VariantProps<typeof toastVariants> {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	duration?: number;
	ref?: React.RefObject<HTMLLIElement | null>;
}

const Toast = ({
	ref,
	className,
	variant,
	open = true,
	onOpenChange,
	duration = TOAST_DURATION,
	...props
}: ToastRootProps) => {
	React.useEffect(() => {
		if (!open || !onOpenChange || duration === Infinity) {
			return;
		}
		const timer = setTimeout(() => {
			onOpenChange(false);
		}, duration);
		return () => {
			clearTimeout(timer);
		};
	}, [open, onOpenChange, duration]);

	return (
		<li
			ref={ref}
			role="status"
			aria-atomic="true"
			data-state={open ? "open" : "closed"}
			className={cn(toastVariants({ variant }), className)}
			{...props}
		/>
	);
};
Toast.displayName = "Toast";

const ToastAction = ({
	ref,
	className,
	...props
}: React.ComponentProps<"button"> & {
	ref?: React.RefObject<HTMLButtonElement | null>;
}) => (
	<button
		ref={ref}
		type="button"
		className={cn(
			"hover:bg-secondary focus:ring-ring group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive inline-flex h-8 shrink-0 items-center justify-center rounded-none border bg-transparent px-3 text-xs font-semibold uppercase tracking-widest transition-colors focus:outline-none focus:ring-1 disabled:pointer-events-none disabled:opacity-50",
			className,
		)}
		{...props}
	/>
);
ToastAction.displayName = "ToastAction";

const ToastClose = ({
	ref,
	className,
	...props
}: React.ComponentProps<"button"> & {
	ref?: React.RefObject<HTMLButtonElement | null>;
}) => (
	<button
		ref={ref}
		type="button"
		aria-label="Close"
		className={cn(
			"text-foreground/50 hover:text-foreground absolute right-1 top-1 rounded-none p-1 opacity-0 transition-opacity focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400",
			className,
		)}
		{...props}
	>
		<XIcon className="h-4 w-4" />
	</button>
);
ToastClose.displayName = "ToastClose";

const ToastTitle = ({
	ref,
	className,
	...props
}: React.ComponentProps<"div"> & {
	ref?: React.RefObject<HTMLDivElement | null>;
}) => (
	<div
		ref={ref}
		className={cn(
			"text-xs font-semibold uppercase tracking-widest [&+div]:text-xs",
			className,
		)}
		{...props}
	/>
);
ToastTitle.displayName = "ToastTitle";

const ToastDescription = ({
	ref,
	className,
	...props
}: React.ComponentProps<"div"> & {
	ref?: React.RefObject<HTMLDivElement | null>;
}) => (
	<div ref={ref} className={cn("text-sm opacity-90", className)} {...props} />
);
ToastDescription.displayName = "ToastDescription";

type ToastProps = ToastRootProps;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
	type ToastProps,
	type ToastActionElement,
	ToastProvider,
	ToastViewport,
	Toast,
	ToastTitle,
	ToastDescription,
	ToastClose,
	ToastAction,
};
